import { Dispatch, useMemo } from 'react';

import { ChakraBox, ChakraFlex } from '@equip.health/ui';
import { DateTime } from 'luxon';

import { AssessmentAction } from '~/lib/constants/patients.constants';
import { stringToJson } from '~/lib/util/survey.util';
import { MaiChatPanel } from '~/components/maud/patients/profile/tabs/overviews/MaiChat';
import PatientOverviewRightRailV2 from '~/components/maud/patients/profile/tabs/overviews/PatientOverviewRightRailV2';
import SurveyResults from '~/components/maud/patients/profile/surveys/SurveyResults';
import { CompServTask } from '~/lib/util/task.util';

export interface PatientOverviewTabV2Props {
  familyTaskCount?: number;
  isTasksError?: boolean;
  maudTaskCount?: number;
  openTasks?: CompServTask[];
  patient?: Patient;
  patientFirstName: string;
  patientId: string;
  patientLastName: string;
  selectedSurveyResults?: Partial<SurveyResponse> | null;
  updateAssessmentState: Dispatch<AssessmentAction>;
}

const PatientOverviewTabV2 = ({
  familyTaskCount,
  isTasksError,
  maudTaskCount,
  openTasks,
  patient,
  patientFirstName,
  patientId,
  patientLastName,
  selectedSurveyResults,
  updateAssessmentState,
}: PatientOverviewTabV2Props) => {
  const patientTasksCount = (maudTaskCount ?? 0) + (familyTaskCount ?? 0);
  // Each count is undefined until its own task response lands.
  const isTasksLoading = maudTaskCount == null || familyTaskCount == null;
  const {
    endDate: selectedAssessmentEndDate,
    jsonResults: selectedAssessmentJsonResults,
    surveyDefinition: selectedSurveyDefinition,
    surveyName: selectedSurveyName,
  } = selectedSurveyResults ?? {};

  const { definition, json } = useMemo(
    () => ({
      definition: stringToJson(selectedSurveyDefinition),
      json: stringToJson(selectedAssessmentJsonResults),
    }),
    [selectedSurveyDefinition, selectedAssessmentJsonResults],
  );

  return (
    <ChakraFlex
      data-testid="patient-overview-tab-v2"
      height="100%"
      overflow="hidden"
    >
      <ChakraBox
        data-testid="patient-overview-tab-v2__chat-column"
        flex="1"
        minWidth={0}
        height="100%"
      >
        <MaiChatPanel
          key={patientId}
          patientFirstName={patientFirstName}
          patientId={patientId}
          patientLastName={patientLastName}
        />
      </ChakraBox>
      <ChakraBox
        data-testid="patient-overview-tab-v2__right-rail"
        width="380px"
        flexShrink={0}
        height="100%"
        // overflowY alone leaves overflowX at auto, so one unbreakable token in a
        // row title would give the whole column a horizontal scrollbar.
        overflowX="hidden"
        overflowY="auto"
        padding="16px 18px 24px"
      >
        {/* Keyed so a patient switch drops the last patient's rows and any tripped boundary. */}
        <PatientOverviewRightRailV2
          isTasksError={isTasksError}
          isTasksLoading={isTasksLoading}
          key={patientId}
          openTasks={openTasks}
          patientId={patientId}
          patientTasksCount={patientTasksCount}
          universalStatusCode={patient?.universalStatusCode}
          updateAssessmentState={updateAssessmentState}
        />
      </ChakraBox>
      <SurveyResults
        isOpen={!!selectedSurveyResults}
        onClose={() =>
          updateAssessmentState({ data: null, field: 'selectedSurveyResults' })
        }
        patientName={`${patientFirstName} ${patientLastName}`}
        surveyCompletionDate={
          !selectedAssessmentEndDate
            ? null
            : DateTime.fromISO(selectedAssessmentEndDate).toFormat('LL/dd/yyyy')
        }
        surveyDefinition={definition}
        surveyName={selectedSurveyName}
        surveyResults={json}
      />
    </ChakraFlex>
  );
};

export default PatientOverviewTabV2;
